import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const QuienSoy = () => {
  return (
    <div className="pt-20 bg-white">
      {/* ========== HERO ========== */}
      <section className="py-16 sm:py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content */}
            <div className="order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 bg-[#c9a227]/10 rounded-full px-4 py-2 mb-6">
                <Sparkles className="w-4 h-4 text-[#c9a227]" />
                <span className="text-sm text-[#c9a227] font-medium">Escuela de Consciencia Akáshica</span>
              </div>
              
              <h1 
                className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[hsl(var(--text-primary))] mb-6 leading-tight"
                data-testid="quien-soy-title"
              >
                Quién soy
              </h1>
              
              <p className="text-lg lg:text-xl text-[hsl(var(--sage))] font-medium mb-8">
                Mi camino y el para qué de lo que comparto
              </p>
              
              <p className="text-lg text-[hsl(var(--text-secondary))] leading-relaxed mb-6">
                Mi nombre es <strong className="text-[hsl(var(--text-primary))]">Herminio</strong>, 
                y mi camino no empezó en la espiritualidad, sino en la estructura.
              </p>
              
              <p className="text-[hsl(var(--text-secondary))] leading-relaxed">
                Me formé como arquitecto técnico, aprendiendo a mirar el mundo desde la lógica, 
                la materia, la estabilidad y la construcción. Aprendí a calcular, a medir, 
                a sostener estructuras para que no se derrumbaran.
              </p>
            </div>
            
            {/* Photo */}
            <div className="order-1 lg:order-2 flex justify-center">
              <div className="relative">
                <div className="w-72 h-80 sm:w-80 sm:h-96 lg:w-[400px] lg:h-[480px] rounded-2xl overflow-hidden shadow-2xl bg-[hsl(var(--sage-light))]">
                  <img
                    src="https://res.cloudinary.com/dpppkdhdk/image/upload/v1776874281/landing_quien_soy_zvd5lh.jpg"
                    alt="Herminio"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 w-32 h-32 rounded-full bg-[#c9a227]/10 -z-10" />
                <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[hsl(var(--sage))]/10 -z-10" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== FIRST LANGUAGE ========== */}
      <section className="py-16 lg:py-20 bg-[#faf8f5]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-xl lg:text-2xl text-[hsl(var(--text-secondary))] leading-relaxed font-light italic">
            "Durante años entendí la vida desde ahí: desde lo que se ve, lo que se apoya en el suelo, 
            lo que tiene forma y función."
          </p>
          <p className="mt-8 text-[hsl(var(--sage))] font-medium">
            Hoy comprendo que esa etapa no fue un error ni una desviación.
            <span className="block mt-2 text-[hsl(var(--text-primary))]">Fue el primer lenguaje de mi alma.</span>
          </p>
        </div>
      </section>

      {/* ========== WHEN STRUCTURE IS NOT ENOUGH ========== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="w-12 h-[2px] bg-[#c9a227] mb-8" />
          
          <h2 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] mb-10">
            Cuando la estructura ya no basta
          </h2>
          
          <div className="space-y-6 text-[hsl(var(--text-secondary))] text-lg leading-relaxed">
            <p>
              Hubo un momento —como les ocurre a muchas personas— en el que la estructura externa 
              dejó de responder a las preguntas internas. Algo en mí empezó a pedir más profundidad, 
              más sentido, más verdad. No desde la mente, sino desde el cuerpo y el sentir.
            </p>
            
            <p className="text-[hsl(var(--sage))] font-medium text-xl">
              Ese llamado no fue repentino ni espectacular.
              <span className="block">Fue silencioso, persistente y honesto.</span>
            </p>
            
            <p>
              Así comenzó mi acercamiento a la sanación energética a través de disciplinas como 
              el reiki, el feng shui, los Registros Akáshicos y otros caminos de consciencia. 
              No llegué a ellos buscando poderes ni experiencias extraordinarias. 
              Llegué buscando comprensión, alivio interno y coherencia.
            </p>
          </div>
        </div>
      </section>

      {/* ========== UNITING WORLDS ========== */}
      <section className="py-20 lg:py-28 bg-[#faf8f5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="w-12 h-[2px] bg-[#c9a227] mb-8" />
          
          <h2 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] mb-10">
            Unir lo que parecía separado
          </h2>
          
          <div className="space-y-6 text-[hsl(var(--text-secondary))] text-lg leading-relaxed">
            <p>
              Con el tiempo entendí que mi verdadero camino no era abandonar la estructura 
              para irme a lo sutil, sino unir ambos mundos.
            </p>
            
            <div className="bg-white rounded-2xl p-8 my-10 shadow-sm">
              <p className="text-xl text-[hsl(var(--text-primary))] font-heading mb-4">
                La arquitectura me enseñó a sostener.
              </p>
              <p className="text-xl text-[hsl(var(--sage))] font-heading">
                La sanación energética me enseñó a escuchar.
              </p>
            </div>
            
            <p>
              Mi propósito empezó a tomar forma cuando comprendí que podía aplicar la misma claridad, 
              orden y responsabilidad con la que se construye un edificio… al mundo interior, 
              al campo energético y a la consciencia humana.
            </p>
            
            <p className="text-[hsl(var(--accent))] font-medium text-xl">
              No me interesaba una espiritualidad etérea.
              <span className="block">Me interesaba una espiritualidad habitable.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ========== FROM SEEKING TO ACCOMPANYING ========== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="w-12 h-[2px] bg-[#c9a227] mb-8" />
          
          <h2 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] mb-10">
            De buscar respuestas a acompañar procesos
          </h2>
          
          <div className="space-y-6 text-[hsl(var(--text-secondary))] text-lg leading-relaxed">
            <p>
              A lo largo de los años he acompañado a muchas personas en procesos profundos de sanación, 
              orden interno y reconexión consigo mismas. Personas sensibles, terapeutas, facilitadores, 
              buscadores sinceros… personas que sentían mucho, pero no siempre sabían cómo sostenerse.
            </p>
            
            <div className="bg-[#faf8f5] rounded-2xl p-8 my-10">
              <p className="text-xl text-[hsl(var(--text-primary))] font-heading mb-4">
                Ahí entendí algo esencial:
              </p>
              <p className="text-lg text-[hsl(var(--text-secondary))]">
                Muchas personas no necesitan más información espiritual.
              </p>
              <p className="text-xl text-[hsl(var(--sage))] font-medium mt-2">
                Necesitan eje, presencia y sostén.
              </p>
            </div>
            
            <p>
              Mi don no está en prometer caminos rápidos, sino en crear espacios seguros 
              donde la verdad interna pueda emerger sin forzarse.
            </p>
          </div>
        </div>
      </section>

      {/* ========== DEEP MEANING ========== */}
      <section className="py-20 lg:py-28 bg-[#faf8f5]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="w-12 h-[2px] bg-[#c9a227] mb-8" />
          
          <h2 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] mb-10">
            El sentido profundo de lo que hago
          </h2>
          
          <div className="space-y-6 text-[hsl(var(--text-secondary))] text-lg leading-relaxed">
            <p>
              Hoy sé que mi camino de vida es acompañar a las personas a reconstruir su casa interior:
            </p>
            
            <ul className="space-y-4 my-8">
              {[
                'Con cimientos en el cuerpo',
                'Con muros de límites sanos',
                'Con espacios de silencio',
                'Y con una consciencia que no huye de la vida, sino que la habita'
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-4">
                  <span className="w-2 h-2 rounded-full bg-[#c9a227] mt-3 flex-shrink-0" />
                  <span className="text-lg">{item}</span>
                </li>
              ))}
            </ul>
            
            <p>
              Trabajo con los Registros Akáshicos y con el campo de información no como algo lejano 
              o místico, sino como una forma amorosa de comprender los patrones que condicionan 
              nuestra forma de vivir, sentir y decidir.
            </p>
            
            <p className="text-[hsl(var(--accent))] font-medium text-xl mt-8">
              Mi propósito no es decirte quién eres.
              <span className="block">Es ayudarte a recordarlo por ti mismo.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ========== THE SCHOOL ========== */}
      <section className="py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <div className="w-12 h-[2px] bg-[#c9a227] mb-8" />
          
          <h2 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] mb-10">
            La Escuela como extensión de mi camino
          </h2>
          
          <div className="space-y-6 text-[hsl(var(--text-secondary))] text-lg leading-relaxed">
            <p>
              La Escuela de Consciencia Akáshica nace como una extensión natural de mi propio recorrido. 
              No es un proyecto separado de mi vida: es la forma que ha tomado mi experiencia 
              para ponerse al servicio de otros.
            </p>
            
            <p>
              Aquí comparto lo que he vivido, lo que sigo integrando y lo que sé que puede ayudar 
              a otras personas a vivir con más calma, coherencia y verdad.
            </p>
            
            <p className="text-[hsl(var(--sage))] font-medium text-xl">
              No desde la perfección.
              <span className="block">Desde la humanidad consciente.</span>
            </p>
          </div>
        </div>
      </section>

      {/* ========== WHY I DO WHAT I DO ========== */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-[hsl(var(--sage-light))]/30 to-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <div className="w-12 h-[2px] bg-[#c9a227] mx-auto mb-8" />
          
          <h2 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] mb-10">
            Por qué hago lo que hago
          </h2>
          
          <p className="text-lg text-[hsl(var(--text-secondary))] mb-10">
            Hago lo que hago porque creo profundamente que:
          </p>
          
          <div className="space-y-6 mb-12">
            {[
              'La consciencia necesita cuerpo',
              'La espiritualidad necesita tierra',
              'Y la verdad necesita amabilidad para poder quedarse'
            ].map((item, index) => (
              <p key={index} className="text-xl lg:text-2xl font-heading text-[hsl(var(--text-primary))]">
                {item}
              </p>
            ))}
          </div>
          
          <div className="bg-white rounded-2xl p-10 shadow-sm max-w-2xl mx-auto">
            <p className="text-lg text-[hsl(var(--text-secondary))] leading-relaxed">
              Y porque sé, por experiencia propia, que cuando una persona recupera su eje interno, 
              su vida empieza a ordenarse de forma natural.
            </p>
          </div>
        </div>
      </section>

      {/* ========== CTA ========== */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl lg:text-4xl font-heading text-[hsl(var(--text-primary))] mb-4">
            ¿Quieres conocer mi forma de trabajar?
          </h2>
          <p className="text-lg text-[hsl(var(--text-secondary))] mb-10 max-w-2xl mx-auto">
            Explora las formaciones disponibles y encuentra el camino que resuene contigo. 
            Sin prisa, a tu ritmo.
          </p>
          <Link to="/formaciones" data-testid="quien-soy-cta">
            <Button className="btn-gold flex items-center gap-2 mx-auto text-base px-8 py-6">
              Ver formaciones
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default QuienSoy;
